import { SelectOption } from 'global';
import { useCallback, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import debounce from 'lodash/debounce';

interface AsyncAutocompleteFieldProps {
  name: string;
  control: any;
  label?: string;
  multiple?: boolean;
  dataTestId?: string;
  extraAction?: (nextValue: SelectOption[]) => void;
  fetchOptions: (query: string) => Promise<SelectOption[]>;
}

export const AsyncAutocompleteField = ({
  name,
  label,
  control,
  extraAction,
  fetchOptions,
  dataTestId,
  multiple = false,
}: AsyncAutocompleteFieldProps) => {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetch = useCallback(
    debounce((query: string) => {
      const currentRequestId = ++requestId.current;

      fetchOptions(query)
        .then(result => {
          if (requestId.current === currentRequestId) {
            setOptions(result);
          }
        })
        .finally(() => {
          if (requestId.current === currentRequestId) {
            setLoading(false);
          }
        });
    }, 300),
    [fetchOptions]
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Autocomplete
          {...field}
          options={options}
          loading={loading}
          multiple={multiple}
          filterOptions={x => x}
          getOptionLabel={o => o.label ?? ''}
          isOptionEqualToValue={(o, v) => o.value === v.value}
          onInputChange={(_, val, reason) => {
            if (reason !== 'input') return;

            if (val.length < 3) {
              debouncedFetch.cancel();
              setOptions([]);
              setLoading(false);
              return;
            }

            setLoading(true);
            debouncedFetch(val);
          }}
          onChange={(_, val) => {
            field.onChange(val);

            if (typeof extraAction === 'function') {
              extraAction(val);
            }
          }}
          renderInput={params => (
            <TextField
              {...params}
              label={label}
              size="small"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              inputProps={{
                ...params.inputProps,
                ...(dataTestId ? { 'data-testid': dataTestId } : {}),
              }}
            />
          )}
        />
      )}
    />
  );
};
